import { motion } from "framer-motion";
import { Check, PhoneMissed } from "lucide-react";
import { Chapter, Reveal } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { ABOUT_IMG } from "../lib/site";
import { useLang } from "../lib/i18n";

export const About = () => {
  const { t, lang } = useLang();
  const smsToday = lang === "nl" ? "Vandaag 09:41" : "Today 9:41 AM";
  const smsDelivered = lang === "nl" ? "Afgeleverd" : "Delivered";
  const smsReply = lang === "nl" ? "Top! Ik plan direct een gesprek." : "Great! I'll book a call right away.";
  return (
  <section id="over-ons" data-testid="about-section" className="relative py-24 md:pt-32 md:pb-20">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12 lg:px-20">
      <div className="lg:col-span-6">
        <Chapter number="01" label={t.chAbout} testid="about-chapter" />
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-archivo text-3xl font-black tracking-tight text-cream md:text-5xl" data-testid="about-heading">
            {t.aboutHeadingA} {t.aboutHeadingB}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 max-w-xl text-sm leading-relaxed text-tan sm:text-base" data-testid="about-copy-1">
            {t.aboutCopy1}
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-tan sm:text-base" data-testid="about-copy-2">
            {t.aboutCopy2}
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="relative mt-10 max-w-md overflow-hidden rounded-[12px] border border-black/10 shadow-2xl shadow-black/20" data-testid="about-image-frame">
            <img src={ABOUT_IMG} alt="Het 4thscenery team in overleg" className="h-56 w-full object-cover" data-testid="about-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-amber/10" />
          </div>
        </Reveal>

        <Reveal delay={0.36}>
          <ul className="mt-6 space-y-2.5" data-testid="about-checks">
            {t.aboutChecks.map((c) => (
              <li key={c} className="flex items-center gap-2.5 text-sm text-tan">
                <Check size={16} className="shrink-0 text-amber" strokeWidth={3} />
                {c}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      <div className="flex items-center lg:col-span-6">
        <Reveal delay={0.2} className="w-full">
          <div className="rounded-[20px] bg-[#181510] p-5 shadow-2xl shadow-black/25 md:p-8" data-testid="about-dark-panel">
          <div className="relative mx-auto w-full max-w-sm">
            <div className="mb-5 flex items-center gap-3">
              <LogoMark size={16} light />
              <p className="font-archivo text-base font-extrabold tracking-tight text-white md:text-lg" data-testid="sms-system-label">{t.smsSystemLabel}</p>
            </div>
            <div className="relative">
            <div className="absolute -top-3 left-6 z-10 rounded-full border border-amber/50 bg-[#181510] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-amber">
              {t.smsBadge}
            </div>
            <div className="rounded-[44px] border border-white/10 bg-black p-2.5 shadow-2xl shadow-black/70" data-testid="sms-demo-card">
              <div className="overflow-hidden rounded-[36px] chat-screen">
                <div className="flex flex-col items-center gap-1 border-b border-white/5 px-4 pb-3.5 pt-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber font-archivo text-xs font-black text-ink">4S</span>
                  <p className="font-archivo text-sm font-bold text-white">4thscenery</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/35">iMessage</p>
                </div>
                <div className="space-y-2 px-4 py-5">
                  <div className="mb-3 flex items-center justify-center gap-1.5 text-[#ff453a]">
                    <PhoneMissed size={12} />
                    <p className="font-mono text-[10px]" data-testid="sms-missed-call-label">{t.smsTitle} · +31 6 •••• •• 42</p>
                  </div>
                  <p className="text-center font-mono text-[10px] text-white/30">{smsToday}</p>
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="max-w-[85%] rounded-[18px] rounded-bl-md bg-[#26262a] px-3.5 py-2.5"
                  >
                    <p className="text-sm leading-relaxed text-[#f5f5f7]" data-testid="sms-demo-message">
                      {t.smsMessage}
                    </p>
                  </motion.div>
                  <p className="font-mono text-[9px] text-white/30">{smsDelivered} · {t.smsAuto}</p>
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.9, duration: 0.6 }}
                    className="ml-auto max-w-[70%] rounded-[18px] rounded-br-md bg-[#0a84ff] px-3.5 py-2.5"
                  >
                    <p className="text-sm leading-relaxed text-white">{smsReply}</p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="flex w-16 items-center justify-center gap-1 rounded-[18px] rounded-bl-md bg-[#26262a] px-3.5 py-3"
                    data-testid="sms-typing-indicator"
                  >
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </motion.div>
                </div>
                <div className="flex items-center gap-2 border-t border-white/5 px-4 pb-4 pt-3">
                  <div className="flex-1 rounded-full border border-white/10 bg-[#1c1c1e] px-4 py-2 text-sm text-white/30">iMessage</div>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber text-[#181510]">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></svg>
                  </span>
                </div>
              </div>
            </div>
            </div>
          </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
